import React, { useState, useMemo } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import EventCard from './EventCard';
import { trackEvent } from '../lib/analytics';
import './EventList.css';

const FILTERS = [
  { label: 'All', id: null },
  { label: 'Music', id: 'KZFzniwnSyZfZ7v7nJ' },
  { label: 'Sports', id: 'KZFzniwnSyZfZ7v7nE' },
  { label: 'Theatre', id: 'KnvZfZ7v7l1' },
  { label: 'Comedy', id: 'KnvZfZ7vAe1' },
];

const EventList = ({
  events,
  loading,
  canSave = false,
  activeEventId,
  setActiveEventId,
  setHoveredEventId,
  startDate,
  interestedEventsById,
  onToggleInterested,
}) => {
  const map = useMap();
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [showInterestedOnly, setShowInterestedOnly] = useState(false);

  const demoCountsByEventId = useMemo(() => {
    const hash = (value) => {
      let total = 0;
      for (let i = 0; i < value.length; i += 1) {
        total = (total * 31 + value.charCodeAt(i)) % 100000;
      }
      return total;
    };
    return events.reduce((acc, event) => {
      const seed = hash(event.id || '');
      const demoInterested = 40 + (seed % 180);
      const demoLottery = 5 + (seed % 45);
      acc[event.id] = {
        interested: demoInterested,
        lottery: demoLottery,
      };
      return acc;
    }, {});
  }, [events]);

  const interestedEvents = useMemo(() => {
    return Object.values(interestedEventsById || {});
  }, [interestedEventsById]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) =>
      new Date(a.dates.start.dateTime || a.dates.start.localDate) -
      new Date(b.dates.start.dateTime || b.dates.start.localDate)
    );
  }, [events]);

  const filteredEvents = useMemo(() => {
    const baseEvents = showInterestedOnly ? interestedEvents : sortedEvents;

    if (!selectedClassification) return baseEvents;

    return baseEvents.filter((event) => {
      const classifications = event.classifications || [];
      return classifications.some((cls) => {
        return (
          cls.segment?.id === selectedClassification ||
          cls.genre?.id === selectedClassification
        );
      });
    });
  }, [
    sortedEvents,
    interestedEvents,
    selectedClassification,
    showInterestedOnly,
  ]);

  let visibleEvents = filteredEvents;

  if (!showInterestedOnly && activeEventId) {
    const activeEvent = events.find((event) => event.id === activeEventId);
    const activeLocation = activeEvent?._embedded?.venues[0]?.location;

    if (activeLocation) {
      const { latitude, longitude } = activeLocation;

      visibleEvents = filteredEvents.filter((event) => {
        const venueLocation = event._embedded?.venues[0]?.location;
        return (
          venueLocation &&
          venueLocation.latitude === latitude &&
          venueLocation.longitude === longitude
        );
      });
    }
  }

  const handleResetView = () => {
    setActiveEventId(null);
    trackEvent('event_list_reset', {
      active_filter: selectedClassification || 'all',
      saved_only: showInterestedOnly,
    });
    if (map) {
      map.setZoom(11);
    }
  };

  const handleFilterClick = (id) => {
    const nextId = selectedClassification === id ? null : id;
    const filter = FILTERS.find((item) => item.id === id);
    setSelectedClassification(nextId);
    trackEvent('filter_clicked', {
      filter_type: 'classification',
      filter_label: nextId ? filter?.label || 'unknown' : 'All',
      filter_value: nextId || 'all',
    });
  };

  const interestedCount = interestedEvents.length;


  return (
    <div className="event-list">
      <div className="event-list-header">
        <p className="results">Upcoming Events: {loading ? 'Loading...' : visibleEvents.length}</p>
        <p style={{ fontStyle: 'italic' }}>*tickets subject to availability</p>

        {/* Filter Buttons */}
        <div className="filter-buttons">
          {FILTERS.map((filter) => (
            <button
              key={filter.label}
              onClick={() => handleFilterClick(filter.id)}
              className={`btn ${selectedClassification === filter.id ? 'btn-primary' : 'btn-light'}`}
            >
              {filter.label}
            </button>
          ))}
          {canSave && (
            <button
              onClick={() => {
                const next = !showInterestedOnly;
                setShowInterestedOnly(next);
                trackEvent('filter_clicked', {
                  filter_type: 'saved_only',
                  filter_label: 'Saved',
                  filter_value: next ? 'on' : 'off',
                });
              }}
              className={`saved-filter-btn ${showInterestedOnly ? 'is-active' : ''}`}
            >
              Saved {interestedCount > 0 ? `(${interestedCount})` : ''}
            </button>
          )}
          {/* <button
            onClick={() => setShowLotteryOnly((prev) => !prev)}
            className={`btn ${showLotteryOnly ? 'btn-primary' : 'btn-light'}`}
          >
            Waitlist {lotteryCount > 0 ? `(${lotteryCount})` : ''}
          </button> */}
        </div>

        {activeEventId && (
          <button className="clear-filter-button" onClick={handleResetView}>
            Show All Events
          </button>
        )}
      </div>

      <div className="event-list-scroll">
        {loading ? (
          <p>Loading events...</p>
        ) : visibleEvents.length > 0 ? (
          visibleEvents.map((event) => (
            <div
              key={event.id}
              className={`event-list-item ${event.id === activeEventId ? 'active' : ''}`}
              onMouseEnter={() => setHoveredEventId(event.id)}
              onMouseLeave={() => setHoveredEventId(null)}
            >
              <EventCard
                event={event}
                searchDate={startDate}
                isInterested={!!interestedEventsById?.[event.id]}
                canSave={canSave}
                interestedCount={
                  (demoCountsByEventId[event.id]?.interested || 0) +
                  (interestedEventsById?.[event.id] ? 1 : 0)
                }
                onToggleInterested={onToggleInterested}
              />
            </div>
          ))
        ) : (
          <p>
            {showInterestedOnly ? 'No saved events yet.' : 'No events found.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default EventList;
